import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HomeIcon from '@mui/icons-material/Home';
import WorkIcon from '@mui/icons-material/Work';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { salesApi } from '../../../../config/apiClient';

// ============================================================================
// INTERFACES
// ============================================================================

interface CustomerAddress {
  id: number;
  label: string;
  address: string;
  isDefault?: boolean;
  district?: string;
  city?: string;
  phone?: string;
  authorizedPerson?: string;
  deliveryNotes?: string;
}

interface CustomerDetailModalProps {
  open: boolean;
  onClose: () => void;
  phone: string;
  customerName?: string;
  accountId?: number;
}

interface CustomerData {
  id: number;
  name: string;
  phone: string;
  mobilePhone?: string;
  email?: string;
  addresses: CustomerAddress[];
  totalOrders: number;
  lastOrderDate?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

const CustomerDetailModal: React.FC<CustomerDetailModalProps> = ({
  open,
  onClose,
  phone,
  customerName,
  accountId,
}) => {
  const { colors: themeColors } = useThemeContext();
  const { userDetails } = useAuth();
  const companyId = userDetails?.companyId;

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customer, setCustomer] = useState<CustomerData | null>(null);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    if (open && phone) {
      loadCustomerData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phone]);

  // ============================================================================
  // FUNCTIONS
  // ============================================================================

  const loadCustomerData = async () => {
    if (!companyId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await salesApi.get(
        `/api/PhoneCall/search-customer/${companyId}?phone=${encodeURIComponent(phone)}`
      );

      const result = response.data;
      const found = result.found || result.Found;

      if (found) {
        const addresses = (result.addresses || result.Addresses || []).map((a: any) => ({
          id: a.id || a.Id,
          label: a.addressLabel || a.AddressLabel || 'Adres',
          address: a.address || a.Address || '',
          district: a.district || a.District || '',
          city: a.city || a.City || '',
          isDefault: a.isDefault ?? a.IsDefault ?? false,
          phone: a.phone || a.Phone,
          authorizedPerson: a.authorizedPerson || a.AuthorizedPerson,
          deliveryNotes: a.deliveryNotes || a.DeliveryNotes,
        }));

        setCustomer({
          id: result.accountId || result.AccountId,
          name: result.customerName || result.CustomerName || 'Bilinmiyor',
          phone: result.phone || result.Phone || phone,
          mobilePhone: result.mobilePhone || result.MobilePhone,
          email: result.email || result.Email,
          addresses: addresses,
          totalOrders: result.totalOrders || result.TotalOrders || 0,
          lastOrderDate: result.lastOrderDate || result.LastOrderDate,
        });
      } else {
        setCustomer(null);
      }
    } catch (err) {
      console.error('Müşteri yükleme hatası:', err);
      setError('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  };

  const getAddressIcon = (label: string) => {
    const lowerLabel = label.toLowerCase();
    if (lowerLabel.includes('iş') || lowerLabel.includes('work')) {
      return <WorkIcon sx={{ color: themeColors.primary }} />;
    }
    return <HomeIcon sx={{ color: themeColors.primary }} />;
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: themeColors.primary,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon />
          <Typography variant="h6">Müşteri Bilgileri</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : customer ? (
          <>
            {/* Müşteri Başlık */}
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Avatar
                sx={{
                  width: 60,
                  height: 60,
                  bgcolor: themeColors.primary,
                  fontSize: '1.5rem',
                }}
              >
                {customer.name.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {customer.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Müşteri ID: #{customer.id}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* İletişim Bilgileri */}
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              İletişim Bilgileri
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <PhoneIcon sx={{ color: themeColors.primary }} />
                </ListItemIcon>
                <ListItemText
                  primary="Telefon"
                  secondary={customer.phone || '-'}
                />
              </ListItem>
              {customer.mobilePhone && (
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon sx={{ color: themeColors.primary }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Cep Telefonu"
                    secondary={customer.mobilePhone}
                  />
                </ListItem>
              )}
              {customer.email && (
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon sx={{ color: themeColors.primary }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="E-posta"
                    secondary={customer.email}
                  />
                </ListItem>
              )}
            </List>

            <Divider sx={{ my: 2 }} />

            {/* Sipariş İstatistikleri */}
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              Sipariş Bilgileri
            </Typography>
            <Box display="flex" gap={2} mb={2}>
              <Chip
                icon={<ReceiptLongIcon />}
                label={`Toplam: ${customer.totalOrders} sipariş`}
                sx={{ bgcolor: `${themeColors.primary}20` }}
              />
              {customer.lastOrderDate && (
                <Chip
                  icon={<CalendarTodayIcon />}
                  label={`Son: ${new Date(customer.lastOrderDate).toLocaleDateString('tr-TR')}`}
                  variant="outlined"
                />
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Adresler */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2" fontWeight="bold">
                Kayıtlı Adresler ({customer.addresses.length})
              </Typography>
            </Box>

            {customer.addresses.length > 0 ? (
              <List dense>
                {customer.addresses.map((addr) => (
                  <ListItem
                    key={addr.id}
                    sx={{
                      border: '1px solid',
                      borderColor: addr.isDefault ? themeColors.primary : 'divider',
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: addr.isDefault ? `${themeColors.primary}10` : 'transparent',
                    }}
                  >
                    <ListItemIcon>
                      {getAddressIcon(addr.label)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography fontWeight="bold">{addr.label}</Typography>
                          {addr.isDefault && (
                            <Chip
                              label="Varsayılan"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                bgcolor: themeColors.primary,
                                color: 'white',
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {addr.district && addr.city 
                              ? `${addr.district}, ${addr.city}` 
                              : ''}
                          </Typography>
                          <Typography variant="body2">
                            {addr.address}
                          </Typography>
                          {addr.authorizedPerson && (
                            <Typography variant="caption" color="text.secondary">
                              Yetkili: {addr.authorizedPerson}
                            </Typography>
                          )}
                          {addr.deliveryNotes && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Not: {addr.deliveryNotes}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Alert severity="info" variant="outlined">
                Kayıtlı adres bulunamadı
              </Alert>
            )}
          </>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{phone}</strong> numarası için kayıtlı müşteri bulunamadı.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Bu numara sisteme kayıtlı değil.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerDetailModal;
